import { useState, useCallback, useMemo, useRef } from "react";
import { parseCommand } from "../engine/terminal/parser";
import { executeCommand } from "../engine/terminal/executor";
import { evaluate } from "../engine/terminal/evaluator";

const LINE_STREAM_DELAY = 65;
const DEFAULT_FETCH_DELAY = 240;

const FETCH_DELAYS = {
  logs: 620, scan: 300, trace: 400, inspect: 420, isolate: 280,
  block: 260, netstat: 560, ps: 490, ping: 820, status: 340,
};

// First output line lands immediately, then a pause before the rest stream in.
const TWO_STEP_PAUSE = { scan: 1300, trace: 1100, block: 900, isolate: 900 };

function buildInitialState(objectives) {
  const objRecord = {};
  for (const obj of objectives ?? []) {
    objRecord[obj.id] = obj.status ?? "pending";
  }
  return {
    assets: {},
    objectives: objRecord,
    elapsed: 0,
    redHerringCount: 0,
    complete: false,
    revealedLogIds: [],
  };
}

// Props: { commandSet, objectives, completionCondition, logEvents, triggeredEvents, onComplete }
// Returns: { lines, history, isStreaming, paletteCommands, submitCommand, objectivesList, assetStates, logFeed }
export function useTerminalEngine({ commandSet, objectives, completionCondition, logEvents, triggeredEvents, onComplete }) {
  // Scenario object for the executor — only commandSet and assetDetails are used
  const scenario = useMemo(
    () => ({ commandSet, assetDetails: {} }),
    [commandSet]
  );

  const [lines, setLines] = useState(() => [{ kind: "blank" }]);
  const [state, setState] = useState(() => buildInitialState(objectives));
  const [history, setHistory] = useState([]);
  const [isStreaming, setIsStreaming] = useState(false);

  const isStreamingRef = useRef(false);
  const stateRef = useRef(null);
  const completedRef = useRef(false);

  // Keep stateRef in sync so submitCommand closure always reads the latest state
  if (stateRef.current === null) {
    stateRef.current = buildInitialState(objectives);
  }

  const appendLines = useCallback((newLines) => {
    setLines((prev) => [...prev, ...newLines]);
  }, []);

  const submitCommand = useCallback(
    (input) => {
      if (isStreamingRef.current) return;

      const current = stateRef.current;
      const promptLine = { kind: "prompt", cmd: input };
      const parseResult = parseCommand(input);

      if (!parseResult.ok) {
        appendLines([promptLine, { kind: "echo", text: parseResult.error }, { kind: "blank" }]);
        return;
      }

      setHistory((h) => [input, ...h]);

      const { command } = parseResult;
      const { output, warnLines, newState } = executeCommand(command, scenario, current);

      setState(newState);
      stateRef.current = newState;
      appendLines([promptLine]);

      isStreamingRef.current = true;
      setIsStreaming(true);

      const delay = FETCH_DELAYS[command.name] ?? DEFAULT_FETCH_DELAY;

      setTimeout(() => {
        const outputLines = output.map((text) => ({ kind: "echo", text }));
        const warnLineItems = warnLines.map((text) => ({ kind: "warn", text }));
        const allLines = [...outputLines, ...warnLineItems];

        const finalize = () => {
          appendLines([{ kind: "blank" }]);
          isStreamingRef.current = false;
          setIsStreaming(false);

          if (!completedRef.current && completionCondition && evaluate(completionCondition, newState)) {
            completedRef.current = true;
            onComplete?.();
          }
        };

        if (allLines.length === 0) {
          finalize();
          return;
        }

        const streamFrom = (startIdx) => {
          let i = startIdx;
          const next = () => {
            if (i < allLines.length) {
              appendLines([allLines[i]]);
              i++;
              setTimeout(next, LINE_STREAM_DELAY);
            } else {
              finalize();
            }
          };
          next();
        };

        const twoStepPause = TWO_STEP_PAUSE[command.name];
        if (twoStepPause && allLines.length > 1) {
          appendLines([allLines[0]]);
          setTimeout(() => streamFrom(1), twoStepPause);
        } else {
          streamFrom(0);
        }
      }, delay);
    },
    [scenario, appendLines, completionCondition, onComplete]
  );

  const paletteCommands = useMemo(() => {
    return commandSet.map((def) => {
      const isAvailable = !def.availableWhen || evaluate(def.availableWhen, state);
      const argSuffix = (def.args ?? []).map((a) => `<${a.name}>`).join(" ");
      const syntax = argSuffix ? `${def.name} ${argSuffix}` : def.name;
      return { name: def.name, syntax, desc: def.description, locked: !isAvailable };
    });
  }, [state, commandSet]);

  // Derive ordered objectives list with live status (done / current / pending)
  const objectivesList = useMemo(() => {
    if (!objectives || objectives.length === 0) return [];
    const firstNonDone = objectives.find((o) => state.objectives[o.id] !== "done");
    return objectives.map((o) => ({
      id: o.id,
      label: o.label,
      status:
        state.objectives[o.id] === "done" ? "done"
        : o.id === firstNonDone?.id ? "current"
        : "pending",
    }));
  }, [objectives, state]);

  // Live log feed: initial events + triggered events revealed by commands
  const logFeed = useMemo(() => {
    const initial = logEvents ?? [];
    const revealed = (state.revealedLogIds ?? [])
      .map((id) => triggeredEvents?.[id])
      .filter(Boolean);
    return [...initial, ...revealed];
  }, [logEvents, triggeredEvents, state.revealedLogIds]);

  return { lines, history, isStreaming, paletteCommands, submitCommand, objectivesList, assetStates: state.assets, logFeed };
}
